import { AlertCircle, Check, Pencil, XCircle } from "lucide-react";

import { cn } from "@utils/ui";

/**
 * How much the extractor managed to read off the uploaded document (MDRS-230).
 *
 * Derived on the frontend from which fields came back — the extract endpoint
 * already returns claim number and examinee name, so "both / one / neither" is
 * enough to tell these apart without a new API field.
 */
export type ParseState = "fully" | "partly" | "nothing" | "edited";

export const parseStateFrom = ({
  claimNumber,
  examineeName,
  edited,
}: {
  claimNumber: string;
  examineeName: string;
  edited: boolean;
}): ParseState => {
  // Edited wins: once the user has corrected a value, what the parser originally
  // managed is no longer the useful thing to report.
  if (edited) return "edited";
  const found = [claimNumber, examineeName].filter((v) => v?.trim()).length;
  if (found === 2) return "fully";
  if (found === 1) return "partly";
  return "nothing";
};

const STATES: Record<
  ParseState,
  { label: string; className: string; Icon: typeof Check }
> = {
  fully: {
    label: "Fully read",
    className: "bg-[#E7F6EC] text-[#16794F]",
    Icon: Check,
  },
  partly: {
    label: "Partly read",
    className: "bg-[#FDF0DC] text-[#B26A00]",
    Icon: AlertCircle,
  },
  nothing: {
    label: "Nothing read",
    className: "bg-[#FDECEC] text-[#B42318]",
    Icon: XCircle,
  },
  edited: {
    label: "Edited",
    className: "bg-[#EEF1F6] text-[#5A5A6B]",
    Icon: Pencil,
  },
};

/** Chip on the "Examinee and claim" card showing what the parser read. */
export const ParseStateChip = ({
  state,
  className,
}: {
  state: ParseState;
  className?: string;
}) => {
  const { label, className: tone, Icon } = STATES[state];

  return (
    <span
      className={cn(
        "inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
        tone,
        className
      )}
    >
      <Icon className="size-3.5" strokeWidth={2.5} />
      {label}
    </span>
  );
};
